import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/LaundryBlogPost.css';

const HEADER_IMG = '/image/laundry_blog_post_0.png';
const FACTORY_IMG = '/image/laundry_blog_post_1.png';
const VIDEO_SRC = '/video/laundry_blog_post_video.mp4';
const VIDEO_POSTER = '/image/laundry_blog_post_0.png';
const OG_IMAGE = 'https://www.nexuni.com/image/laundry_blog_post_0.png';
const CANONICAL_ROUTE = '/blog/laundry-gr00t';

const SEO_TITLE =
  'Develop AI Robots for Singapore Laundry Factory Automation with NVIDIA Isaac GR00T 1.7 | Nexuni';
const SEO_DESC =
  'Nexuni is partnering with NVIDIA to build next-generation autonomous robots for a large laundry factory in Singapore, using the NVIDIA Isaac GR00T platform and the GR00T 1.7 vision-language-action model to automate drying, industrial ironing, and precision garment folding for continuous 24/7 operation.';

/**
 * Dedicated long-read blog article: Nexuni × NVIDIA laundry factory automation.
 * Content is intentionally English-only across all locales (zh / en / ja);
 * only the surrounding chrome (Navbar, Breadcrumb, Footer) is localized.
 */
class LaundryBlogPost extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      visible: {}
    };
    this.update_lang = this.update_lang.bind(this);
    this.navigate = this.navigate.bind(this);
    this.observerCallback = this.observerCallback.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.sectionRefs = [];
    this.progressRef = React.createRef();
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setSEO();
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.14,
      rootMargin: '0px 0px -60px 0px'
    });
    this.sectionRefs.forEach(ref => ref && this.observer.observe(ref));
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  setSEO() {
    const BASE = 'https://www.nexuni.com';
    const slug = this.state.lang === 'zh' ? 'zh-tw' : this.state.lang;
    document.documentElement.setAttribute('lang', slug === 'zh-tw' ? 'zh-TW' : slug);
    document.title = SEO_TITLE;

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector('meta[' + attr + '="' + name + '"]');
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', SEO_DESC);
    const canonical = BASE + '/' + slug + CANONICAL_ROUTE;
    const link = document.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute('href', canonical);

    setMeta('og:title', SEO_TITLE, 'property');
    setMeta('og:description', SEO_DESC, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:image', OG_IMAGE, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', SEO_TITLE);
    setMeta('twitter:description', SEO_DESC);
    setMeta('twitter:image', OG_IMAGE);
  }

  onScroll() {
    const el = this.progressRef.current;
    if (!el) return;
    const doc = document.documentElement;
    const scrolled = doc.scrollTop || document.body.scrollTop;
    const height = doc.scrollHeight - doc.clientHeight || 1;
    el.style.width = Math.min(100, Math.max(0, (scrolled / height) * 100)) + '%';
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) updates[entry.target.dataset.reveal] = true;
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visible: { ...prev.visible, ...updates } }));
    }
  }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.reveal = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  update_lang(newLang) {
    this.props.updateLang(newLang);
  }

  navigate(route) {
    window.location.href = buildPath(this.state.lang, route);
  }

  render() {
    const lang = this.state.lang;
    const vis = this.state.visible;
    const rv = id => (vis[id] ? ' lbp_in' : '');

    return (
      <div className={'lbp_page ' + lang}>
        <div className="lbp_progress" aria-hidden="true">
          <span ref={this.progressRef} className="lbp_progress_bar" />
        </div>

        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb
          lang={lang}
          items={[
            { label: { zh: '部落格', en: 'Blog', ja: 'ブログ' }, route: '/blog' },
            { label: { zh: '洗衣工廠自動化', en: 'Laundry Factory Automation', ja: 'ランドリー工場の自動化' } }
          ]}
        />

        {/* ===== Hero ===== */}
        <header className="lbp_hero">
          <div className="lbp_hero_grid" aria-hidden="true" />
          <div className="lbp_hero_glow" aria-hidden="true" />
          <div className="lbp_hero_inner">
            <a className="lbp_back" href={buildPath(lang, '/blog')}>&larr; Back to Blog</a>
            <h1 className="lbp_title">
              Develop AI Robots for Singapore Laundry Factory Automation with NVIDIA Isaac GR00T 1.7
            </h1>
            <p className="lbp_deck">
              Nexuni and NVIDIA are building next-generation autonomous robots for a large-scale laundry factory in
              Singapore, turning heavy manual workflows into automated production lines running around the clock.
            </p>
            <div className="lbp_meta">
              <span className="lbp_meta_brand">Nexuni <em>&times;</em> NVIDIA</span>
              <span className="lbp_meta_dot" />
              <span>July 7, 2026</span>
              <span className="lbp_meta_dot" />
              <span>4 min read</span>
            </div>
          </div>
        </header>

        {/* ===== Featured image ===== */}
        <div className="lbp_featured">
          <figure
            className={'lbp_figure lbp_reveal' + rv('feat')}
            ref={el => this.registerRef(el, 'feat')}
          >
            <img src={HEADER_IMG} alt="Nexuni dual-arm robot practicing garment manipulation in the training setup, shown with multiple synchronized camera views." loading="eager" />
            <figcaption>
              <span className="lbp_cap_tick" />
              Nexuni&rsquo;s dual-arm robot practices garment manipulation in our training setup, observed through
              synchronized multi-camera views for data collection.
            </figcaption>
          </figure>
        </div>

        {/* ===== Article ===== */}
        <article className="lbp_article">
          <p
            className={'lbp_lede lbp_reveal' + rv('lede')}
            ref={el => this.registerRef(el, 'lede')}
          >
            Nexuni is partnering with NVIDIA to build next-generation autonomous robots for a large laundry factory in
            Singapore. The goal is to achieve continuous 24/7 operation across the facility. These intelligent systems
            handle complex, high-dexterity tasks, including laundry drying, industrial ironing, and precision
            garment folding. Deploying these robots directly to the factory floor helps solve regional labor shortages
            and increases efficiency by turning manual workflows into automated production lines.
          </p>

          <figure
            className={'lbp_figure lbp_figure_inline lbp_reveal' + rv('factory')}
            ref={el => this.registerRef(el, 'factory')}
          >
            <img src={FACTORY_IMG} alt="Workers performing high-volume industrial ironing inside a Singapore laundry factory." loading="lazy" />
            <figcaption>
              <span className="lbp_cap_tick" />
              Inside the Singapore laundry factory: high-volume industrial ironing, drying, and folding, the
              manual workflows these robots are learning to automate.
            </figcaption>
          </figure>

          <section
            className={'lbp_section lbp_reveal' + rv('sec1')}
            ref={el => this.registerRef(el, 'sec1')}
          >
            <h2 className="lbp_h2"><span className="lbp_h2_mark" />A complete pipeline, from human demonstrations to the factory floor</h2>
            <p>
              Nexuni uses the NVIDIA Isaac GR00T development platform and the new GR00T 1.7 model to run these
              workflows. This setup creates a complete pipeline for physical data collection, simulation training,
              evaluation, and real-world deployment. The NVIDIA Isaac GR00T 1.7 vision-language-action model features a Cosmos
              Reason2 2B backbone. Because it is pretrained on tens of thousands of hours of human demonstrations, it
              helps robots execute complex tasks with natural motion. Nexuni trains the model using massive amounts of
              data of garment manipulation, giving the robots expert-level mastery over laundry operations and
              accelerating deployment.
            </p>
          </section>

          <figure
            className={'lbp_figure lbp_figure_inline lbp_video lbp_reveal' + rv('video')}
            ref={el => this.registerRef(el, 'video')}
          >
            <div className="lbp_video_frame">
              <video controls playsInline preload="metadata" poster={VIDEO_POSTER}>
                <source src={VIDEO_SRC} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <figcaption>
              <span className="lbp_cap_tick" />
              Testing our GR00T 1.7-based policy in Nexuni&rsquo;s training setup.
            </figcaption>
          </figure>

          <section
            className={'lbp_section lbp_reveal' + rv('sec2')}
            ref={el => this.registerRef(el, 'sec2')}
          >
            <h2 className="lbp_h2"><span className="lbp_h2_mark" />Setting a new standard for commercial laundry automation</h2>
            <p>
              Together, Nexuni and NVIDIA are setting a new standard for automation in commercial services. By combining
              Nexuni&rsquo;s expertise in real-world AI robotic system integration with NVIDIA&rsquo;s advanced robotics
              platform, this project creates a scalable blueprint for smart service facilities worldwide. Moving forward,
              the companies plan to expand this physical AI infrastructure to other high-volume sectors,
              demonstrating how autonomous workflows can safely and efficiently transform heavy manual labor on a global
              scale.
            </p>
          </section>
        </article>

        <Footer lang={lang} navigate={this.navigate} />
      </div>
    );
  }
}

export default LaundryBlogPost;
